import { useEffect, useMemo, useState } from 'react'
import { FeatureDemo } from 'components/feature-demo/FeatureDemo'
import {
  featureDemoStorageId,
  shouldShowFeatureDemo,
  writeFeatureDemoDismissed,
  type FeatureDemoChoice,
} from 'components/feature-demo/featureDemoStorage'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { useAuth } from 'hooks/auth/useAuth'

export const COMMAND_PALETTE_DEMO_ID = 'command-palette'

const INTRO = {
  title: 'Search Is Now a Command Palette',
  desktopBody:
    'Search the portal from one field. Press ⌘ K (Ctrl K on Windows) or type / for commands — or click the search bar.',
  mobileBody:
    'Search the portal from one field. Tap the search icon, then type / for commands.',
}

const DEMO_STEPS = [
  {
    title: 'Slash Commands',
    body: 'Type / to list commands. /inventory, /lead, /invoice, /client, and /transport search one list. /goto jumps to a page.',
    query: '/',
    open: true,
  },
  {
    title: 'Scope a Search',
    body: 'After /inventory, type a VIN or stock number. Only trucks show. The same idea works for leads and invoices.',
    query: '/inventory ',
    open: true,
  },
  {
    title: 'Search Everything',
    body: 'Skip the slash to search commands, pages, and records together. Arrow keys move, Enter opens, Esc closes.',
    query: '',
    open: true,
  },
] as const

interface CommandPaletteOnboardingProps {
  anchorEl: HTMLElement | null
  isAdmin: boolean
  isMobile?: boolean
  onOpenPalette: (seed?: string) => void
  onSetQuery: (query: string) => void
  onClosePalette: () => void
  onVisibilityChange?: (visible: boolean) => void
}

export function CommandPaletteOnboarding({
  anchorEl,
  isAdmin,
  isMobile = false,
  onOpenPalette,
  onSetQuery,
  onClosePalette,
  onVisibilityChange,
}: CommandPaletteOnboardingProps) {
  const { currentUser, userInfo } = useAuth()
  const enabled = FEATURE_FLAGS.commandPaletteOnboarding && isAdmin
  const storageId = featureDemoStorageId(
    COMMAND_PALETTE_DEMO_ID,
    currentUser?.uid || userInfo?.id,
  )
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)
  const [demoIndex, setDemoIndex] = useState<number | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 400)
    return () => window.clearTimeout(id)
  }, [])

  useEffect(() => {
    if (!ready || !anchorEl) return
    if (!currentUser?.uid && !userInfo?.id) return
    setVisible(shouldShowFeatureDemo(storageId, { enabled }))
  }, [ready, anchorEl, enabled, storageId, currentUser?.uid, userInfo?.id])

  useEffect(() => {
    onVisibilityChange?.(visible)
  }, [visible, onVisibilityChange])

  const step = demoIndex === null ? null : DEMO_STEPS[demoIndex]
  const stepLabel = useMemo(() => {
    if (demoIndex === null) return 'New'
    return `Step ${demoIndex + 1} of ${DEMO_STEPS.length}`
  }, [demoIndex])

  useEffect(() => {
    if (!step) return
    if (step.open) onOpenPalette(step.query)
    else onSetQuery(step.query)
  }, [step, onOpenPalette, onSetQuery])

  const dismiss = (choice: FeatureDemoChoice) => {
    writeFeatureDemoDismissed(storageId, choice)
    setVisible(false)
    setDemoIndex(null)
    onClosePalette()
  }

  if (!visible || !anchorEl) return null

  if (step && demoIndex !== null) {
    const isLast = demoIndex === DEMO_STEPS.length - 1
    return (
      <FeatureDemo
        open
        anchorEl={anchorEl}
        title={step.title}
        body={step.body}
        stepLabel={stepLabel}
        primaryLabel={isLast ? 'Done' : 'Next'}
        secondaryLabel={demoIndex > 0 ? 'Back' : undefined}
        tertiaryLabel='Skip'
        onPrimary={() => {
          if (isLast) dismiss('completed')
          else setDemoIndex((index) => (index ?? 0) + 1)
        }}
        onSecondary={demoIndex > 0 ? () => setDemoIndex((index) => (index ?? 1) - 1) : undefined}
        onTertiary={() => dismiss('skipped')}
        placement={isMobile ? 'bottom' : 'right-start'}
      />
    )
  }

  return (
    <FeatureDemo
      open
      anchorEl={anchorEl}
      title={INTRO.title}
      body={isMobile ? INTRO.mobileBody : INTRO.desktopBody}
      stepLabel='New'
      primaryLabel='Show Me'
      secondaryLabel='Got It'
      onPrimary={() => setDemoIndex(0)}
      onSecondary={() => dismiss('skipped')}
    />
  )
}
